import { Component } from '@theme/component';

/** @type {boolean} One-time guard for the document-level Orbe modal listeners */
let orbeModalCloseEnhanced = false;

/** @returns {HTMLElement|null} Orbe's geolocation modal container, only while visible */
function visibleOrbeModal() {
	const container = /** @type {HTMLElement|null} */ (document.querySelector('.md-modal__container'));
	if (!container || container.getClientRects().length === 0) return null;
	return container;
}

function closeOrbeModal() {
	if (typeof window.orbito?.closeModal === 'function') {
		window.orbito.closeModal('md-app-embed__modal');
		return;
	}
	/** @type {HTMLElement|null} */ (document.querySelector('.md-modal-closeButtonAction'))?.click();
}

/**
 * Orbe's geolocation modal ships its own close button but doesn't close on
 * Escape or backdrop click — add both, once per page.
 */
function enhanceOrbeModalClose() {
	if (orbeModalCloseEnhanced) return;
	orbeModalCloseEnhanced = true;

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') return;
		if (visibleOrbeModal()) closeOrbeModal();
	});

	document.addEventListener('click', (event) => {
		const container = visibleOrbeModal();
		if (!container) return;

		const target = /** @type {Element} */ (event.target);
		const dialog = container.querySelector('.md-modal__dialog');
		if (container.contains(target) && dialog && !dialog.contains(target)) {
			closeOrbeModal();
		}
	});
}

/**
 * Footer Country Selector — Orbe injection hook host.
 *
 * The Orbe geolocation app (Shopify Markets) injects its switcher widget into
 * this block's hook div (targeted by ID from the Orbe dashboard). While the
 * fallback is showing (widget not injected), clicking anywhere on the block —
 * label or value — opens Orbe's Geolocation Popup via its JS API
 * (`orbito.openModalWithoutRefresh()`, requires the popup app embed). When
 * Orbe has replaced the hook content, its own widget owns the interaction and
 * this component stays out of the way.
 *
 * Also upgrades the server-rendered fallback value ("Germany / EN") to the
 * "Deutschland / Germany / EN" format via Intl.DisplayNames.
 *
 * @attr {string} data-country-iso - Current country ISO code (e.g. 'DE')
 * @attr {string} data-language-iso - Current language ISO code (e.g. 'en')
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [value]
 *
 * @extends {Component<Refs>}
 */
class FooterCountrySelector extends Component {
	connectedCallback() {
		super.connectedCallback();
		this.#renderValue();
		this.addEventListener('click', this.#handleClick);
		enhanceOrbeModalClose();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener('click', this.#handleClick);
	}

	/**
	 * Opens Orbe's Geolocation Popup. Tries the call chain the legacy theme
	 * uses in production (orbito.openAndRefreshModal, the mdApp_ global from
	 * the app embed) before the newer documented API. No-op when Orbe isn't
	 * loaded (app embed disabled).
	 */
	openSelector() {
		if (typeof window.orbito?.openAndRefreshModal === 'function') {
			window.orbito.openAndRefreshModal();
			return;
		}
		if (typeof window.mdApp_openAndRefreshModal === 'function') {
			window.mdApp_openAndRefreshModal();
			return;
		}
		window.orbito?.openModalWithoutRefresh?.();
	}

	/**
	 * Delegated click for the label / whitespace around the value button.
	 * Only acts in fallback mode — once Orbe injects its widget into the hook
	 * (our value ref is gone), Orbe owns all interaction.
	 * @param {MouseEvent} event
	 */
	#handleClick = (event) => {
		if (!this.refs.value?.isConnected) return;
		if (/** @type {Element} */ (event.target).closest?.('button') === this.refs.value) return;
		this.openSelector();
	};

	#renderValue() {
		const { countryIso, languageIso } = this.dataset;
		const { value } = this.refs;
		if (!countryIso || !languageIso || !value || value.childElementCount > 0) return;

		try {
			const local = new Intl.DisplayNames([countryIso.toLowerCase()], { type: 'region' }).of(countryIso);
			const english = new Intl.DisplayNames(['en'], { type: 'region' }).of(countryIso);
			const parts = local === english ? [english] : [local, english];
			value.textContent = `${parts.join(' / ')} / ${languageIso.toUpperCase()}`;
		} catch {
			// keep the server-rendered fallback
		}
	}
}

if (!customElements.get('footer-country-selector')) {
	customElements.define('footer-country-selector', FooterCountrySelector);
}
